import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddIconCircle, DeleteIcon } from 'assets';
import { Input, Textarea } from 'components/form';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';

type TechnicalSkillFormData = {
  technicalSkills: {
    title: string;
    expectation: string;
  }[];
};

const technicalSkillSchema = yup.object().shape({
  technicalSkills: yup.array().of(
    yup.object().shape({
      title: yup.string().required('Skill name is required'),
      expectation: yup.string().required('Expectation is required'),
    })
  ).required('At least one technical skill is required').min(1, 'At least one technical skill is required'),
});

interface TechnicalSkillsEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: TechnicalSkillFormData;
  onSave: (data: TechnicalSkillFormData) => void;
  isSubmitting?: boolean;
}

export const TechnicalSkillsEditModal: React.FC<TechnicalSkillsEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting
}) => {
  const defaultValues: TechnicalSkillFormData = currentData || { technicalSkills: [] };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset
  } = useForm<TechnicalSkillFormData>({
    defaultValues,
    resolver: yupResolver(technicalSkillSchema)
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'technicalSkills'
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentData, reset]);

  const onSubmit = (data: TechnicalSkillFormData) => {
    onSave(data);
  };

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Technical skills"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      <div className="font-[Gelion] w-[90%] mx-auto my-[15px]">
        <h3 className='text-[#1D2939] font-semibold text-lg mb-2'>Technical Skills</h3>
        {fields.map((item, index) => (
          <div
            key={item.id}
            className="relative pb-4 mb-4 border-b border-[#EAECF0] last:border-b-0 last:mb-0 last:pb-0 [&_label]:text-[#667085] [&_label]:text-sm [&_label]:font-normal"
          >
            {fields.length > 1 && (
              <div className={`absolute top-0 right-0 z-[2] group cursor-pointer ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
                <DeleteIcon onClick={() => remove(index)} />
                <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                  <span className="block whitespace-nowrap bg-[#1d2939] text-white text-xs rounded-lg px-3 py-2 leading-relaxed">
                    Delete this competence
                  </span>
                  <span className="absolute top-full right-[8px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
                </div>
              </div>
            )}
            <Input
              placeholder=""
              label="Name of Competence"
              type="text"
              required
              validatorMessage={errors.technicalSkills?.[index]?.title?.message ?? ""}
              name={`technicalSkills.${index}.title`}
              register={register}
              className="!border !border-[#D0D5DD] !rounded-md !bg-white"
              parentClassName='!mb-3'
            />
            <Textarea
              placeholder=""
              label="Expectations"
              required
              validatorMessage={errors.technicalSkills?.[index]?.expectation?.message ?? ""}
              name={`technicalSkills.${index}.expectation`}
              register={register}
              className="!border !border-[#D0D5DD] !rounded-md !bg-white !h-[80px] !min-h-[60px]"
              parentClassName='!mb-0'
            />
          </div>
        ))}

        <button
          type="button"
          className="mt-4 px-6 py-2 bg-[var(--primary-blue)] text-white font-medium border-none rounded cursor-pointer uppercase transition-colors duration-200 flex items-center gap-1.5 hover:bg-[#003087]"
          onClick={() => append({ title: '', expectation: '' })}
        >
          <AddIconCircle /> Add Technical Skill
        </button>
      </div>
    </BaseEditModal>
  );
};
